import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './UniGraphics/reducers';
import { historyMiddleware } from './UniGraphics/Middlewares/History.middleware';
import MiniCart from './UniGraphics/Components/MiniCart';
import QuickSearch from './UniGraphics/Components/QuickSearch';
import Navigation from './UniGraphics/Components/Navigation';
import FacetedSearch from './UniGraphics/Components/FacetedSearch';
import FacetedSearchCompactContainer from './UniGraphics/Components/FacetedSearchCompactContainer';
import DynamicComponent from './UniGraphics/Components/DynamicComponent';
import NoNameCustomProductModal from './UniGraphics/Components/NoNameCustomProductModal';
import SortDropdown from './UniGraphics/Components/SortDropdown';
import FilterTags from './UniGraphics/Components/FilterTags';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    const quickSearchContainers = document.querySelectorAll('#quickSearch');
    if (quickSearchContainers.length > 0) {
        quickSearchContainers.forEach((container) =>
            renderReact(
                <Provider store={store}>
                    <QuickSearch />
                </Provider>,
                container
            )
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('selectedFilter')) {
        renderReact(
            <Provider store={store}>
                <FilterTags />
            </Provider>,
            document.getElementById('selectedFilter')
        );
    }
    if (document.querySelectorAll('.sort-dropdown-react').length > 0) {
        Array.from(document.querySelectorAll('.sort-dropdown-react')).forEach(
            (container) => {
                renderReact(
                    <Provider store= {store}>
                        <SortDropdown />
                    </Provider>,
                    container
                );
            }
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./UniGraphics/Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./UniGraphics/Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./UniGraphics/Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./UniGraphics/Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('a')
                ).map((link) => {
                    const img = link.querySelector('img');
                    return {
                        src: link.dataset.src,
                        alt: img ? img.alt : ''
                    };
                });
                const thumbnails = Array.from(
                    rootElement.querySelectorAll('a img')
                ).map((img) => ({
                    src: img.src,
                    alt: img.alt || ''
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages
                            images={images}
                            thumbnails={thumbnails}
                        />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./UniGraphics/Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./UniGraphics/Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                    articleType
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                                articleType
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('.toggle-vat-container').length > 0) {
        const ToggleVat = DynamicComponent({
            loader: () => import('./UniGraphics/Components/ToggleVat'),
        });
        Array.from(document.querySelectorAll('.toggle-vat-container')).forEach(
            (toggleVat) => {
                const {
                    showPriceWithVat
                } = toggleVat.dataset;
                renderReact(
                    <Provider store={store}>
                        <ToggleVat
                            {...{
                                showPriceWithVat
                            }}
                        />
                    </Provider>,
                    toggleVat
                );
            }
        );
    }

    if (document.querySelectorAll('.order-article-form').length > 0) {
        const OrderArticleForm = DynamicComponent({
            loader: () => import('./UniGraphics/Components/OrderArticleForm'),
        });
        Array.from(document.querySelectorAll('.order-article-form')).forEach(
            (orderArticleForm) => {
                const {
                    articleNumber
                } = orderArticleForm.dataset;
                renderReact(
                    <Provider store={store}>
                        <OrderArticleForm {...{
                            articleNumber
                        }} />
                    </Provider>,
                    orderArticleForm
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./UniGraphics/Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('noNameCustomProductModal')) {
        renderReact(
            <Provider store={store}>
                <NoNameCustomProductModal />
            </Provider>,
            document.getElementById('noNameCustomProductModal')
        );
    }

    if (document.querySelectorAll('.quantity-field').length > 0) {
        const QuantityField = DynamicComponent({
            loader: () => import('./UniGraphics/Components/QuantityField.container')
        });
        Array.from(document.querySelectorAll('.quantity-field')).forEach((field) => {
            const { fieldId, maxvalue, errorMessageMaxvalue } = field.dataset;
                ReactDOM.render(
                <QuantityField {...{ fieldId, maxvalue, errorMessageMaxvalue  }} />,
                    field
                );
            }
        );
    }

    if (document.querySelectorAll('.js-toggle-read-more').length > 0) {
        const ToggleReadMore = DynamicComponent({
            loader: () => import('./UniGraphics/Components/ToggleReadMore'),
        });
        Array.from(document.querySelectorAll('.js-toggle-read-more')).forEach(
            (container) => {
                const {
                    targetSelector,
                } = container.dataset;
                renderReact(
                    <ToggleReadMore
                        targetSelector={targetSelector}
                    />,
                    container
                );
            }
        );
    }

    // Initialize drag-to-scroll for popular categories block
    if (document.querySelectorAll('.scroll-container').length > 0) {
        Array.from(document.querySelectorAll('.scroll-container')).forEach((container) => {
                let isDown = false;
                let isDragging = false;
                let startX;
                let scrollLeft;
            const parentBlock = container.closest('.scroll-container-parent');

                // Prevent native image drag on all images within container
                container.querySelectorAll('img').forEach((img) => {
                    img.setAttribute('draggable', 'false');
                img.addEventListener('dragstart', (e) => e.preventDefault());
                });

                // Prevent native drag on links
                container.querySelectorAll('a').forEach((link) => {
                link.addEventListener('dragstart', (e) => e.preventDefault());
                });

                container.addEventListener('mousedown', (e) => {
                    isDown = true;
                    isDragging = false;
                    container.style.cursor = 'grabbing';
                    startX = e.pageX - container.offsetLeft;
                    scrollLeft = container.scrollLeft;
                });

                container.addEventListener('mouseleave', () => {
                    isDown = false;
                    container.style.cursor = 'grab';
                    if (parentBlock) {
                    parentBlock.classList.remove('scroll-container--dragging');
                    }
                });

                container.addEventListener('mouseup', () => {
                    isDown = false;
                    container.style.cursor = 'grab';

                    // Remove dragging class after a short delay to allow click prevention
                    setTimeout(() => {
                        if (parentBlock) {
                        parentBlock.classList.remove('scroll-container--dragging');
                        }
                    }, 50);
                });

                container.addEventListener('mousemove', (e) => {
                    if (!isDown) return;
                    e.preventDefault();
                    const x = e.pageX - container.offsetLeft;
                    const walk = (x - startX) * 1.5; // Scroll speed multiplier

                    if (Math.abs(walk) > 5 && !isDragging) {
                        isDragging = true;
                        if (parentBlock) {
                        parentBlock.classList.add('scroll-container--dragging');
                        }
                    }

                    container.scrollLeft = scrollLeft - walk;
                });

                // Prevent click on links when dragging
            container.addEventListener('click', (e) => {
                        if (isDragging) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
            }, true);

                // Touch support for mobile devices
                let touchStartX;
                let touchScrollLeft;

            container.addEventListener('touchstart', (e) => {
                        touchStartX = e.touches[0].pageX - container.offsetLeft;
                        touchScrollLeft = container.scrollLeft;
            }, { passive: true });

            container.addEventListener('touchmove', (e) => {
                        if (!touchStartX) return;
                        const x = e.touches[0].pageX - container.offsetLeft;
                        const walk = (x - touchStartX) * 1.2;
                        container.scrollLeft = touchScrollLeft - walk;
            }, { passive: true });

            container.addEventListener('touchend', () => {
                        touchStartX = null;
            }, { passive: true });
        });
    }

    // Auto-scroll for header top row list on mobile
    const headerTopRowList = document.querySelector('.header__top-row-list');
    if (headerTopRowList && window.innerWidth <= 1199) {
        let currentIndex = 0;
        const items = headerTopRowList.querySelectorAll('li');

        if (items.length > 1) {
            const autoScroll = () => {
                currentIndex = (currentIndex + 1) % items.length;
                const targetItem = items[currentIndex];
                const listRect = headerTopRowList.getBoundingClientRect();
                const itemRect = targetItem.getBoundingClientRect();
                const scrollOffset = itemRect.top - listRect.top - (listRect.height / 2) + (itemRect.height / 2);
                headerTopRowList.scrollBy({
                    top: scrollOffset,
                    behavior: 'smooth'
                });
            };

            setInterval(autoScroll, 3000);
        }
    }
};

bootstrapComponents();
